import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  MdSentimentVeryDissatisfied,
  MdSentimentDissatisfied,
  MdSentimentNeutral,
  MdSentimentSatisfied,
  MdSentimentVerySatisfied,
} from "react-icons/md";
import Translate from "../components/Navbar/translate/Translate";
import logoClient from "../images/logo.png";
import headerImage from "../images/header.png";

import { FcGoogle } from "react-icons/fc";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./QrLanding.css";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:1234"
    : "https://api.cliclaim.com");

/* ---------- helpers ---------- */
const compressImage = (file, maxSizeMB = 1) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/") || file.size <= maxSizeMB * 1024 * 1024) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        const MAX_DIM = 1600;
        if (width > height && width > MAX_DIM) {
          height = Math.round(height * (MAX_DIM / width));
          width = MAX_DIM;
        } else if (height > MAX_DIM) {
          width = Math.round(width * (MAX_DIM / height));
          height = MAX_DIM;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          0.8
        );
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
};

/* ---------- styled ---------- */
const Page = styled.main`
  padding: 24px 20px 40px;
  max-width: 980px;
  margin: 0 auto;
  color: #e5e7eb;
  overflow-x: hidden;
`;

const Background = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #060b12 url(${headerImage}) center / cover no-repeat;
  position: relative;
  isolation: isolate;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 0;
  }
  > * {
    position: relative;
    z-index: 1;
  }
  overflow-x: hidden;
`;

const TopBar = styled.div`
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px;
  width: 100%;
  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  @media (max-width: 640px) {
    width: 90px;
    height: 90px;
  }
`;

const Card = styled.section`
  background: #0b1220;
  border: 1px solid #111827;
  border-radius: 14px;
  padding: 24px 20px;
  margin: 10px auto;
  width: min(100%, 980px);
  box-sizing: border-box;
`;

const Title = styled.h1`
  color: #fff;
  font-weight: 800;
  margin: 0 0 6px;
  line-height: 1.1;
`;

const Subtitle = styled.p`
  color: #9ca3af;
  margin: 0 0 18px;
`;

const Form = styled.form`
  display: grid;
  gap: 12px;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
  label {
    color: #b7c5d1;
    font-size: 14px;
  }
  input[type="text"],
  input[type="email"],
  input[type="tel"],
  input[type="file"],
  textarea {
    width: 100%;
    background: rgba(20, 25, 32, 0.75);
    color: #e7edf3;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    outline: none;
    padding: 12px 14px;
    font-size: 15px;
    transition: border 0.15s, box-shadow 0.15s, background 0.2s;
  }
  textarea {
    min-height: 110px;
    resize: vertical;
  }
  input:focus,
  textarea:focus {
    border-color: rgba(0, 230, 142, 0.65);
    box-shadow: 0 0 0 3px rgba(0, 230, 142, 0.15);
  }
`;

const ErrorText = styled.div`
  color: #fca5a5;
  font-size: 13px;
`;

const PhoneWrap = styled.div`
  .PhoneInput {
    width: 100%;
    background: rgba(20, 25, 32, 0.75);
    color: #e7edf3;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 8px 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .PhoneInputCountry {
    display: flex;
    align-items: center;
  }
  .PhoneInputInput {
    background: transparent;
    color: #e7edf3;
    border: none;
    outline: none;
    width: 100%;
    font-size: 15px;
    padding: 6px 8px;
    direction: ${(p) => (p.$rtl ? "rtl" : "ltr")};
    text-align: ${(p) => (p.$rtl ? "right" : "left")};
  }
  .PhoneInput:focus-within {
    border-color: rgba(0, 230, 142, 0.65);
    box-shadow: 0 0 0 3px rgba(0, 230, 142, 0.15);
  }
`;

const Button = styled.button`
  appearance: none;
  border: none;
  background: #18e68e;
  color: #0b1220;
  font-weight: 700;
  font-size: 16px;
  padding: 12px 16px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.06s, box-shadow 0.2s, opacity 0.2s;
  &:hover {
    box-shadow: 0 10px 24px rgba(0, 230, 142, 0.25);
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
  }
`;

const Secondary = styled.button`
  background: rgba(255, 255, 255, 0.08);
  color: #e7edf3;
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
`;

/* modal */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(3, 8, 14, 0.6);
  display: grid;
  place-items: center;
  z-index: 50;
`;

// const Modal = styled.div`
//   width: min(520px, 92vw);
//   background: rgba(11, 15, 20, 0.95);
//   border: 1px solid rgba(255, 255, 255, 0.08);
//   border-radius: 16px;
//   padding: 20px;
// `;

const ModalTitle = styled.h3`
  margin: 0 0 8px;
  font-weight: 800;
`;

const ModalText = styled.p`
  margin: 0 0 14px;
  color: #b7c5d1;
  white-space: pre-line;
`;

// const ModalActions = styled.div`
//   display: flex;
//   justify-content: flex-end;
//   gap: 10px;
// `;

const CloseBtn = styled(Button)`
  background: #18e68e;
  color: #0b1220;
`;

const Modal = styled.div`
  width: min(420px, calc(100vw - 24px));
  background: rgba(11, 15, 20, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 22px 18px 18px;
  box-sizing: border-box;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);

  @media (max-width: 480px) {
    width: calc(100vw - 20px);
    padding: 20px 16px 16px;
    border-radius: 18px;
  }
`;

// const ModalTitle = styled.h3`
//   margin: 0 0 10px;
//   font-weight: 800;
//   font-size: 24px;
//   line-height: 1.2;
//   color: #ffffff;

//   @media (max-width: 480px) {
//     font-size: 20px;
//   }
// `;

// const ModalText = styled.p`
//   margin: 0 0 18px;
//   color: #d1d5db;
//   font-size: 16px;
//   line-height: 1.5;

//   @media (max-width: 480px) {
//     font-size: 15px;
//   }
// `;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ModalBtn = styled.button`
  width: 100%;
  min-height: 54px;
  border-radius: 999px;
  border: none;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 18px;
  transition: transform 0.06s ease, box-shadow 0.2s ease, opacity 0.2s ease;

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }

  @media (max-width: 480px) {
    min-height: 52px;
    font-size: 16px;
  }
`;

const SkipBtn = styled(ModalBtn)`
  background: rgba(255, 255, 255, 0.08);
  color: #f3f4f6;
  border: 1px solid rgba(255, 255, 255, 0.14);
`;

const GoogleBtn = styled(ModalBtn)`
  background: #22e392;
  color: #081018;
  box-shadow: 0 10px 24px rgba(34, 227, 146, 0.22);
`;

const GoogleIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
`;

const ToastItem = styled.div`
  background: ${(p) => {
    if (p.$type === "error") return "#ef4444";
    if (p.$type === "warning") return "#f59e0b";
    return "#10b981";
  }};
  color: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-weight: 600;
  font-size: 14px;
  pointer-events: auto;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const EmojiContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const EmojiButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: transform 0.2s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    transform: scale(1.1);
  }

  svg {
    width: 32px;
    height: 32px;
    transition: color 0.2s;
  }
`;

/* ---------- component ---------- */
export default function QrLanding() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [search, setSearch] = useState(window.location.search);
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const presetCompanyId = params.get("companyId") || null; // claimToken from QR
  const table = params.get("table") || null;

  // form state
  const [companyName, setCompanyName] = useState("");
  const [companyServices, setCompanyServices] = useState([]);
  const [companyDetailsBusy, setCompanyDetailsBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMsg] = useState("");
  const [file, setFile] = useState(null);
  const [rating, setRating] = useState(0);
  const [busy, setBusy] = useState(false);

  const emojiIcons = useMemo(
    () => [
      { icon: MdSentimentVeryDissatisfied, color: "#f44336", val: 1 },
      { icon: MdSentimentDissatisfied, color: "#ff9800", val: 2 },
      { icon: MdSentimentNeutral, color: "#ffeb3b", val: 3 },
      { icon: MdSentimentSatisfied, color: "#8bc34a", val: 4 },
      { icon: MdSentimentVerySatisfied, color: "#18E68E", val: 5 },
    ],
    []
  );

  // geo
  const [, setGeoState] = useState("idle");

  // file refs
  const camRef = useRef(null);
  const galleryRef = useRef(null);

  // success modal
  const [okOpen, setOkOpen] = useState(false);

  // toast
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info") => {
    const normalizedMessage = Array.isArray(message) ? message.join(", ") : message;
    const id = Date.now() + Math.random();

    setToasts((prev) => [...prev, { id, message: normalizedMessage, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  // preview
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!file || !file.type?.startsWith("image/")) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const placeholders = useMemo(
    () => ({
      firstName: t("qr.form.firstName_ph") || "First name",
      lastName: t("qr.form.lastName_ph") || "Last name",
      email: t("qr.form.email_ph") || "Email",
      phone: t("qr.form.phone_ph") || "Phone",
      message: t("qr.form.message_ph") || "Message",
    }),
    [t]
  );

  const phoneInputProps = useMemo(
    () => ({
      name: "phone",
      id: "phone",
      inputMode: "tel",
      autoComplete: "tel",
      "aria-label": t("qr.form.phone_label") || "Phone",
    }),
    [t]
  );

  useEffect(() => {
    const sanitizeName = (value) =>
      (value || "").toString().replace(/[^\p{L}]/gu, "");

    const applyIdentityToForm = ({ email: e, firstname, lastname }) => {
      if (e) setEmail(e);
      if (firstname) setFirstName(sanitizeName(firstname));
      if (lastname) setLastName(sanitizeName(lastname));
    };

    const pathname = (window.location.pathname || "").toLowerCase();
    const isLandingSuccess = pathname.includes("/auth/google/landing/success");

    const emailFromParams = params.get("email") || "";
    const firstFromParams =
      params.get("firstname") || params.get("firstName") || "";
    const lastFromParams =
      params.get("lastname") || params.get("lastName") || "";

    if (isLandingSuccess && (emailFromParams || firstFromParams || lastFromParams)) {
      setAuthBusy(true);
      applyIdentityToForm({
        email: emailFromParams,
        firstname: firstFromParams,
        lastname: lastFromParams,
      });
      sessionStorage.setItem(
        "landing_identity",
        JSON.stringify({
          email: emailFromParams,
          firstname: firstFromParams,
          lastname: lastFromParams,
        })
      );

      const savedCompanyId = sessionStorage.getItem("qr_companyId") || "";
      const savedTable = sessionStorage.getItem("qr_table") || "";
      const nextParams = new URLSearchParams();
      if (savedCompanyId) nextParams.set("companyId", savedCompanyId);
      if (savedTable) nextParams.set("table", savedTable);
      const nextUrl = `/claim${nextParams.toString() ? `?${nextParams.toString()}` : ""}`;
      window.history.replaceState({}, "", nextUrl);
      setSearch(window.location.search);
      setAuthOpen(false);
      setAuthBusy(false);
      return;
    }

    const storedIdentityRaw = sessionStorage.getItem("landing_identity");
    if (storedIdentityRaw && !email && !firstName && !lastName) {
      try {
        const stored = JSON.parse(storedIdentityRaw);
        applyIdentityToForm({
          email: stored?.email,
          firstname: stored?.firstname,
          lastname: stored?.lastname,
        });
      } catch { }
    }

    const skipPrompt = sessionStorage.getItem("skip_google_prompt") === "1";
    const hasStoredIdentity = Boolean(sessionStorage.getItem("landing_identity"));
    if (!skipPrompt && !hasStoredIdentity) setAuthOpen(true);
  }, [email, firstName, lastName, params]);

  const handleAuthorizeClick = useCallback(async () => {
    if (!("geolocation" in navigator)) {
      setGeoState("error");
      showToast(t("qr.errors.geo_not_supported"), "error");
      return null;
    }

    const isSecureOrigin =
      window.isSecureContext ||
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost";

    if (!isSecureOrigin) {
      setGeoState("error");
      showToast(
        t("qr.errors.insecure_origin"),
        "error"
      );
      return null;
    }

    setGeoState("asking");

    const getPos = (opts) =>
      new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, opts)
      );

    try {
      const p = await getPos({
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      });

      const got = {
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        accuracy: p.coords.accuracy,
      };

      setGeoState("granted");
      return got;
    } catch (err) {
      try {
        const p = await getPos({
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 0,
        });

        const got = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
        };

        setGeoState("granted");
        return got;
      } catch (err2) {
        setGeoState("denied");
        return null;
      }
    }
  }, [showToast, t]);

  /* watch permission changes */
  useEffect(() => {
    let perm;

    (async () => {
      try {
        if (navigator.permissions?.query) {
          perm = await navigator.permissions.query({ name: "geolocation" });
          const onChange = async () => {
            if (perm.state === "granted") {
              await handleAuthorizeClick();
            } else if (perm.state === "prompt") {
              setGeoState("prompt");
            } else if (perm.state === "denied") {
              setGeoState("denied");
            }
          };
          perm.onchange = onChange;
        }
      } catch { }
    })();

    return () => {
      if (perm) perm.onchange = null;
    };
  }, [handleAuthorizeClick]);

  /* auto attempt on mount */
  useEffect(() => {
    (async () => {
      await handleAuthorizeClick();
    })();
  }, [handleAuthorizeClick]);

  /* fetch company public details */
  useEffect(() => {
    let cancelled = false;

    const pickCompanyName = (data) =>
      data?.company_name ||
      data?.business_name ||
      data?.companyName ||
      data?.company?.company_name ||
      data?.company?.business_name ||
      data?.company?.companyName ||
      data?.company?.name ||
      data?.name ||
      "";

    const pickCompanyServices = (data) => {
      const raw =
        data?.services ||
        data?.company?.services ||
        data?.company_services ||
        data?.company?.company_services ||
        [];

      if (!Array.isArray(raw)) return [];

      return raw
        .map((item) => {
          if (!item) return null;
          if (typeof item === "string") return { _id: item, name: item };
          if (typeof item === "object") {
            const id = item._id || item.id || item.serviceId || item.name;
            const name = item.name || item.label || item.title || "";
            if (!name) return null;
            return { _id: id, name };
          }
          return null;
        })
        .filter(Boolean);
    };

    const fetchCompanyPublicDetails = async (claimToken) => {
      if (!claimToken) return;

      setCompanyDetailsBusy(true);

      try {
        const res = await axios.get(
          `${API_BASE}/company/public-details/${claimToken}`
        );

        if (cancelled) return;

        console.log("📥 /company/public-details response:", res?.data);
        const name = pickCompanyName(res?.data);
        if (name) setCompanyName(name);
        setCompanyServices(pickCompanyServices(res?.data));
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          t("qr.errors.company_load_failed") ||
          t("qr.errors.generic");
        showToast(msg, "error");
      } finally {
        if (!cancelled) setCompanyDetailsBusy(false);
      }
    };

    if (presetCompanyId) {
      fetchCompanyPublicDetails(presetCompanyId);
    }

    return () => {
      cancelled = true;
    };
  }, [presetCompanyId, showToast, t]);

  useEffect(() => {
    if (!Array.isArray(companyServices) || companyServices.length === 0) {
      setSelectedServiceIds([]);
      return;
    }
    setSelectedServiceIds((prev) =>
      prev.filter((id) =>
        companyServices.some((s) => String(s._id) === String(id))
      )
    );
  }, [companyServices]);

  /* submit */
  const onSubmit = async (e) => {
    e.preventDefault();

    const normalizeName = (value) => value.replace(/[^\p{L}]/gu, "");
    const validName = (value) => value.length >= 3;

    const cleanFirstName = normalizeName(firstName);
    const cleanLastName = normalizeName(lastName);

    if (cleanFirstName !== firstName) setFirstName(cleanFirstName);
    if (cleanLastName !== lastName) setLastName(cleanLastName);

    if (!validName(cleanFirstName) || !validName(cleanLastName)) {
      setFirstNameTouched(true);
      setLastNameTouched(true);
      showToast(
        t("qr.errors.name_invalid") ||
        "Prénom et nom: uniquement des lettres et minimum 3 lettres.",
        "error"
      );
      return;
    }

    setBusy(true);

    try {
      // Toujours demander la localisation au moment du submit
      showToast(
        t("qr.toast.location_needed_to_submit"),
        "info"
      );

      const currentCoords = await handleAuthorizeClick();

      if (!currentCoords) {
        showToast(
          t("qr.errors.location_permission_required"),
          "error"
        );
        setBusy(false);
        return;
      }

      const fd = new FormData();
      fd.append("firstName", cleanFirstName);
      fd.append("lastName", cleanLastName);
      fd.append("email", email);
      fd.append("phone", phone || "");
      fd.append("claim_message", message);
      fd.append("finalRate", String(rating));
      fd.append("user_lat", String(currentCoords.lat));
      fd.append("user_lng", String(currentCoords.lng));

      if (table) {
        fd.append("tableNb", table);
      }

      const normalizedSelectedServiceIds = Array.from(
        new Set(selectedServiceIds.map(String).filter(Boolean))
      );
      console.log("📤 selected services:", normalizedSelectedServiceIds);
      if (normalizedSelectedServiceIds.length > 0) {
        fd.append("serviceId", normalizedSelectedServiceIds[0]);
      }
      if (normalizedSelectedServiceIds.length > 1) {
        fd.append("serviceIds", JSON.stringify(normalizedSelectedServiceIds));
      }

      // IMPORTANT:
      // Ici on envoie claimToken au lieu de company
      // car le back-end doit retrouver la company via claimToken
      if (presetCompanyId) {
        fd.append("claimToken", presetCompanyId);
      } else {
        // fallback ancien flow sans QR : nearest
        const nearestPayload = {
          lat: currentCoords.lat,
          lng: currentCoords.lng,
          accuracy: currentCoords.accuracy,
        };

        console.log("📤 /company/nearest payload:", nearestPayload);
        const nearestRes = await axios.post(
          `${API_BASE}/company/nearest`,
          nearestPayload
        );

        const { companyId, services } = nearestRes.data || {};

        if (!companyId || !services || services.length === 0) {
          showToast(t("qr.errors.no_company"), "error");
          setBusy(false);
          return;
        }

        // Si ton back-end ancien flow attend company MongoId
        fd.append("company", companyId);
        fd.append("serviceId", services[0]);
      }

      try {
        if (typeof fd.entries === "function") {
          const entries = Array.from(fd.entries()).map(([k, v]) => {
            if (v instanceof File) {
              return [k, { name: v.name, type: v.type, size: v.size }];
            }
            return [k, v];
          });
          console.log("📤 /claim/public payload (FormData entries):", entries);
        }
      } catch (fdErr) {
        console.log("⚠️ Could not inspect FormData entries:", fdErr);
      }

      console.log("📤 /claim/public submit (preview):", {
        firstName: cleanFirstName,
        lastName: cleanLastName,
        email,
        phone: phone || "",
        claim_message: message,
        finalRate: rating,
        user_lat: currentCoords.lat,
        user_lng: currentCoords.lng,
        tableNb: table || null,
        claimToken: presetCompanyId || null,
        selectedServiceIds: normalizedSelectedServiceIds,
        hasFile: Boolean(file),
      });

      const res = await axios.post(`${API_BASE}/claim/public`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (
        res?.data?.statusCode === 400 &&
        typeof res?.data?.message === "string" &&
        res.data.message.includes("Distance dépasse")
      ) {
        showToast(t("qr.errors.too_far"), "error");
        setBusy(false);
        return;
      }

      if (res.data && (res.data.existing || res.data.error)) {
        showToast(res.data.message || t("qr.errors.generic"), "error");
        setBusy(false);
        return;
      }

      const claimId = res.data.claimId || res.data._id || res.data.id;
      const token = res.data.token;

      if (file && claimId) {
        if (typeof claimId === "object") {
          showToast(t("qr.errors.invalid_claim_id"), "warning");
        } else {
          try {
            let fileToUpload = file;
            if (file.type.startsWith("image/")) {
              try {
                fileToUpload = await compressImage(file, 0.8);
              } catch (e) { }
            }

            const fileFd = new FormData();
            fileFd.append("files", fileToUpload);

            const headers = token
              ? { Authorization: `Bearer ${token}` }
              : undefined;

            console.log("📤 /claim/:id/add-files payload (file):", {
              claimId,
              file: { name: file.name, type: file.type, size: file.size },
              hasToken: Boolean(token),
            });
            await axios.post(`${API_BASE}/claim/${claimId}/add-files`, fileFd, {
              ...(headers ? { headers } : {}),
            });
          } catch (uploadErr) {
            const status =
              uploadErr?.response?.status ??
              uploadErr?.response?.data?.statusCode ??
              null;
            const backendMsg = uploadErr?.response?.data?.message;
            const backendMsgText = Array.isArray(backendMsg)
              ? backendMsg.join(", ")
              : backendMsg;

            console.log("❌ /claim/:id/add-files error:", {
              url: `${API_BASE}/claim/${claimId}/add-files`,
              status,
              data: uploadErr?.response?.data,
              message: uploadErr?.message,
            });

            showToast(
              backendMsgText ||
              (status ? `${t("qr.errors.file_upload_failed")} (${status})` : t("qr.errors.file_upload_failed")),
              "warning"
            );
          }
        }
      }

      setOkOpen(true);
      setFirstName("");
      setLastName("");
      setFirstNameTouched(false);
      setLastNameTouched(false);
      setEmail("");
      setPhone("");
      setMsg("");
      setFile(null);
      setRating(0);

      if (camRef.current) camRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    } catch (err) {
      const statusCode = err?.response?.data?.statusCode;
      const backendMessage = err?.response?.data?.message;
      const backendMessageText = Array.isArray(backendMessage)
        ? backendMessage.join(", ")
        : backendMessage;

      if (
        statusCode === 400 &&
        typeof backendMessageText === "string" &&
        backendMessageText.includes("Distance dépasse")
      ) {
        showToast(t("qr.errors.too_far"), "error");
        return;
      }

      const msg =
        backendMessageText || err?.message || t("qr.errors.generic");

      showToast(msg, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Background>
      <TopBar dir="ltr">
        <Logo src={logoClient} alt="CliClaim" />
        <Translate />
      </TopBar>

      <Card>
        <Title className="titre_claim text-claim-center">
          You <span className="title-span">C</span>li
          <span className="title-span">C</span> <br />
          You <span className="title-span">C</span>laim
        </Title>

        <Page dir={isRTL ? "rtl" : "ltr"}>
          <Subtitle>{t("qr.subhead")}</Subtitle>

          {/* {presetCompanyId && (
            <p style={{ color: "#9dd9b9", marginTop: -6 }}>
              {t("qr.preset_company") || "Entreprise détectée via QR"}
            </p>
          )} */}




          {/* {table && (
            <p style={{ color: "#9dd9b9", marginTop: -8 }}>
              {(t("qr.table_label") || "Table")} {table}
            </p>
          )} */}

          <Form onSubmit={onSubmit}>
            <Field>
              <label>{t("qr.form.firstName_label") || "First name"}</label>
              <input
                key="firstName"
                type="text"
                required
                minLength={3}
                placeholder={placeholders.firstName}
                value={firstName}
                onChange={(e) =>
                  setFirstName(e.target.value.replace(/[^\p{L}]/gu, ""))
                }
                onBlur={() => setFirstNameTouched(true)}
                autoComplete="given-name"
              />
              {firstNameTouched && firstName.length > 0 && firstName.length < 3 && (
                <ErrorText>
                  {t("qr.form.name_min_error") || "Minimum 3 lettres."}
                </ErrorText>
              )}
            </Field>

            <Field>
              <label>{t("qr.form.lastName_label") || "Last name"}</label>
              <input
                key="lastName"
                type="text"
                required
                minLength={3}
                placeholder={placeholders.lastName}
                value={lastName}
                onChange={(e) =>
                  setLastName(e.target.value.replace(/[^\p{L}]/gu, ""))
                }
                onBlur={() => setLastNameTouched(true)}
                autoComplete="family-name"
              />
              {lastNameTouched && lastName.length > 0 && lastName.length < 3 && (
                <ErrorText>
                  {t("qr.form.name_min_error") || "Minimum 3 lettres."}
                </ErrorText>
              )}
            </Field>

            <Field>
              <label>{t("qr.form.email_label") || "Email"}</label>
              <input
                key="email"
                type="email"
                required
                placeholder={placeholders.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputMode="email"
                autoComplete="email"
              />
            </Field>

            <Field>
              <label>{t("qr.form.phone_label") || "Phone"}</label>
              <PhoneWrap $rtl={isRTL}>
                <div dir="ltr">
                  <PhoneInput
                    key="phone"
                    international
                    defaultCountry="TN"
                    countryCallingCodeEditable={false}
                    value={phone}
                    onChange={setPhone}
                    placeholder={placeholders.phone}
                    inputProps={phoneInputProps}
                  />
                </div>
              </PhoneWrap>
            </Field>

            {presetCompanyId && (
              <Field>
                <label>{t("privacy.defs.company_label") || "Company"}</label>
                <input
                  type="text"
                  readOnly
                  value={companyName || (companyDetailsBusy ? "..." : presetCompanyId)}
                />
              </Field>
            )}

            {table && (
              <Field>
                <label>{t("qr.table_label") || "Table"}</label>
                <input type="text" readOnly value={table} />
              </Field>
            )}

            {companyServices.length > 0 && (
              <Field>
                <label>{t("qr.form.services_label") || "Services"}</label>
                <div className="services-grid">
                  {companyServices.map((s) => {
                    const id = String(s._id);
                    const selected = selectedServiceIds.some((x) => String(x) === id);
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`service-btn${selected ? " service-btn--active" : ""}`}
                        aria-pressed={selected}
                        onClick={() => {
                          setSelectedServiceIds((prev) => {
                            const normalizedPrev = prev.map(String);
                            if (normalizedPrev.includes(id)) {
                              return normalizedPrev.filter((x) => x !== id);
                            }
                            return [...normalizedPrev, id];
                          });
                        }}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}


            <Field>
              <label>{t("qr.form.rating_label") || "Satisfaction"}</label>
              <EmojiContainer>
                {emojiIcons.map((item, idx) => (
                  <EmojiButton
                    key={idx}
                    type="button"
                    onClick={() => setRating(item.val)}
                  >
                    <item.icon
                      color={item.val <= rating ? item.color : "#595a5d"}
                    />
                  </EmojiButton>
                ))}
              </EmojiContainer>
            </Field>

            <Field>
              <label>{t("qr.form.message_label") || "Message"}</label>
              <textarea
                key="message"
                required
                placeholder={placeholders.message}
                value={message}
                onChange={(e) => setMsg(e.target.value)}
              />
            </Field>

            <Field>
              <label>{t("qr.form.file_label") || "File"}</label>

              <input
                ref={camRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />

              <input
                ref={galleryRef}
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/webp"
                style={{ display: "none" }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />

              {!file ? (
                <div className="file-actions">
                  <Secondary
                    type="button"
                    onClick={() => galleryRef.current?.click()}
                  >
                    🖼️ {t("qr.form.choose_gallery") || "Choose from gallery"}
                  </Secondary>

                  <Secondary
                    type="button"
                    onClick={() => camRef.current?.click()}
                  >
                    📷 {t("qr.form.take_photo") || "Take photo"}
                  </Secondary>
                </div>
              ) : (
                <div
                  style={{
                    position: "relative",
                    marginTop: "10px",
                    display: "inline-block",
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "12px",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        padding: "12px",
                        background: "rgba(255,255,255,0.08)",
                        borderRadius: "12px",
                        color: "#e7edf3",
                      }}
                    >
                      📄 {file.name}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      if (camRef.current) camRef.current.value = "";
                      if (galleryRef.current) galleryRef.current.value = "";
                    }}
                    style={{
                      position: "absolute",
                      top: "-10px",
                      [isRTL ? "left" : "right"]: "-10px",
                      background: "#ef4444",
                      border: "2px solid #0b1220",
                      color: "white",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                    }}
                  >
                    🗑️
                  </button>
                </div>
              )}
            </Field>

            <Button
              type="submit"
              disabled={busy || firstName.length < 3 || lastName.length < 3}
            >
              {busy
                ? t("qr.form.sending") || "Sending..."
                : t("qr.form.send") || "Send"}
            </Button>
          </Form>
        </Page>
      </Card>

      {okOpen && (
        <Backdrop onClick={() => {
          setOkOpen(false);
          setRating(0);
        }}>
          <Modal dir={isRTL ? "rtl" : "ltr"} onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t("qr.modal.title") || "Success"}</ModalTitle>
            <ModalText>
              {t("qr.modal.text") || "Votre réclamation a été envoyée avec succès."}
            </ModalText>
            <ModalActions>
              <CloseBtn onClick={() => {
                setOkOpen(false);
                setRating(0);
              }}>
                {t("qr.modal.close") || "Close"}
              </CloseBtn>
            </ModalActions>
          </Modal>
        </Backdrop>
      )}

      {authOpen && (
        <Backdrop
          onClick={() => {
            sessionStorage.setItem("skip_google_prompt", "1");
            setAuthOpen(false);
          }}
        >
          <Modal dir={isRTL ? "rtl" : "ltr"} onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {t("qr.auth.prompt_title") || "Connexion avec Google"}
            </ModalTitle>

            <ModalText>
              {t("qr.auth.prompt_text") ||
                "Souhaitez-vous vous connecter avec Google pour préremplir vos informations ?"}
            </ModalText>

            <ModalActions>
              <GoogleBtn
                type="button"
                onClick={() => {
                  setAuthBusy(true);
                  sessionStorage.setItem("qr_companyId", presetCompanyId || "");
                  sessionStorage.setItem("qr_table", table || "");
                  window.location.href = `${API_BASE}/auth/google/landing`;
                }}
                disabled={authBusy}
              >
                <GoogleIconWrap>
                  <FcGoogle size={24} />
                </GoogleIconWrap>
                {t("qr.auth.google_btn") || "Continuer avec Google"}
              </GoogleBtn>

              <SkipBtn
                type="button"
                onClick={() => {
                  sessionStorage.setItem("skip_google_prompt", "1");
                  setAuthOpen(false);
                }}
                disabled={authBusy}
              >
                {t("qr.auth.skip_btn") || "Continuer sans Google"}
              </SkipBtn>
            </ModalActions>
          </Modal>
        </Backdrop>
      )}

      <ToastContainer>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} $type={toast.type}>
            {toast.message}
          </ToastItem>
        ))}
      </ToastContainer>
    </Background>
  );
}
